import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Generic game engine hook. Drives the common lifecycle for all game modes:
 *   idle → running (first click) → game over (mode-specific condition) → idle
 *
 * modeConfig shape:
 *   {
 *     initialModeState: any,
 *     onClickUpdate(modeState, timeArray): newModeState,
 *     isGameOver({ clickCount, timeArray, modeState }): boolean,
 *     computeScore(timeArray, modeState): scoreObject,
 *   }
 *
 * onGameOver(score, timeArray) is called once when the game ends.
 */
const useGameEngine = (modeConfig, onGameOver) => {
  const [isRunning, setIsRunning] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [timeArray, setTimeArray] = useState([]);
  const [modeState, setModeState] = useState(modeConfig.initialModeState);

  // Keep refs to avoid stale closures – the handler always reads the latest version
  const modeConfigRef = useRef(modeConfig);
  useEffect(() => { modeConfigRef.current = modeConfig; });

  // When the mode config changes (e.g. switching game mode), reset modeState to the new initial value
  useEffect(() => {
    const initState = modeConfig.initialModeState;
    modeStateRef.current = initState;
    setModeState(initState);
  }, [modeConfig]);

  const onGameOverRef = useRef(onGameOver);
  useEffect(() => { onGameOverRef.current = onGameOver; });

  // Refs that mirror state so handleClick never captures stale values
  const isRunningRef = useRef(false);
  const clickCountRef = useRef(0);
  const timeArrayRef = useRef([]);
  const modeStateRef = useRef(modeConfig.initialModeState);

  // handleClick is stable (empty dep array) – it reads/writes refs synchronously
  // then drives React state for rendering
  const handleClick = useCallback(() => {
    const timeNow = performance.now();

    if (!isRunningRef.current) {
      const initModeState = modeConfigRef.current.initialModeState;
      isRunningRef.current = true;
      clickCountRef.current = 1;
      timeArrayRef.current = [timeNow];
      modeStateRef.current = initModeState;
      setIsRunning(true);
      setClickCount(1);
      setTimeArray([timeNow]);
      setModeState(initModeState);
      return;
    }

    const updatedTimeArray = [...timeArrayRef.current, timeNow];
    const newClickCount = clickCountRef.current + 1;
    const updatedModeState = modeConfigRef.current.onClickUpdate(modeStateRef.current, updatedTimeArray);

    timeArrayRef.current = updatedTimeArray;
    clickCountRef.current = newClickCount;
    modeStateRef.current = updatedModeState;

    setTimeArray(updatedTimeArray);
    setClickCount(newClickCount);
    setModeState(updatedModeState);

    if (modeConfigRef.current.isGameOver({ clickCount: newClickCount, timeArray: updatedTimeArray, modeState: updatedModeState })) {
      isRunningRef.current = false;
      setIsRunning(false);
      const score = modeConfigRef.current.computeScore(updatedTimeArray, updatedModeState);
      onGameOverRef.current(score, updatedTimeArray);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    isRunningRef.current = false;
    clickCountRef.current = 0;
    timeArrayRef.current = [];
    modeStateRef.current = modeConfigRef.current.initialModeState;
    setIsRunning(false);
    setClickCount(0);
    setTimeArray([]);
    setModeState(modeConfigRef.current.initialModeState);
  }, []);

  return { isRunning, clickCount, timeArray, modeState, handleClick, reset };
};

export default useGameEngine;
